import uuid from 'uuid';

import requirementsParser from './requirementsParser';
import CKEditorLoader from './libraries/CKEDitor';
import MomentTimezone from './libraries/MomentTimezone';
import SymbolicMathLoader from './libraries/SymbolicMath';
import PDFJSLoader from './libraries/PDFJS';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';

const generalResourcesCacher = {
    /**
     * Extracts all questions from an assessment.
     * @param {Object} assessment - The assessment object containing sections and questions.
     * @returns {Array} - A flat array of all questions in the assessment.
     */
    getAllQuestions(assessment) {
        return assessment.sections.reduce((questionsArr, section) => {
            return questionsArr.concat(section.questions);
        }, []);
    },

    /**
     * Caches a set of files using the provided filePackageCacher.
     * @param {Array} files - The files to cache.
     * @param {string} title - The title for the cache package.
     * @param {Function} filePackageCacher - The function responsible for caching the file package.
     * @returns {Promise} - A promise that resolves when the files are cached.
     */
    async cacheFiles(files, title, filePackageCacher) {
        return filePackageCacher({
            files,
            title,
            id: uuid.v4(),
            type: 'general',
        });
    },

    /**
     * Checks requirements and caches all necessary general resources for an assessment.
     * @param {Object} assessment - The assessment object.
     * @param {Function} filePackageCacher - The function responsible for caching file packages.
     * @returns {Promise} - A promise that resolves when all caching operations are completed.
     */
    async cacheAll(assessment, filePackageCacher) {
        const questions = this.getAllQuestions(assessment);
        const promisedDependencies = [];

        promisedDependencies.push(MomentTimezone.getAllFilesToCache());

        /* HANDLED BY PRE-RENDERING
        if (requirementsParser.isAceEditorRequired(questions)) {
            const filesToCache = ACEEditorLoader.getAllFilesToCache(questions);
            promisedDependencies.push(
                generalResourcesCacher.cacheFiles(filesToCache, 'ACE Editor resources', filePackageCacher),
            );
        }

        if (requirementsParser.isMathJaxRequired(questions)) {
            const filesToCache = MathJaxLoader.getAllFilesToCache();
            promisedDependencies.push(
                generalResourcesCacher.cacheFiles(filesToCache, 'MathJax resources', filePackageCacher),
            );
        }

        // No point running this if cacheHandler === sw. All PDFJS files get requested by an html file on aws.
        if (requirementsParser.isPdfJsRequired(questions)) {
            promisedDependencies.push(
                generalResourcesCacher.cacheFiles(PDFJSLoader.getAllFilesToCache(), 'PDFJS resources', filePackageCacher),
            );
        }
        */

        if (requirementsParser.isCkEditorRequired(questions)) {
            promisedDependencies.push(
                generalResourcesCacher.cacheFiles(CKEditorLoader.getAllFilesToCache(), 'CKEditor resources', filePackageCacher),
            );
        }

        if (requirementsParser.isCkEditorRequired(questions)
            && marketplacePropertiesService.isOfflineResilienceForCkEditorEnabled()) {
            promisedDependencies.push(
                CKEditorLoader.cacheFiles(),
            );
        }

        if (requirementsParser.isPdfJsRequired(questions)
            && marketplacePropertiesService.isOfflineResilienceForPdfViewerEnabled()) {
            promisedDependencies.push(
                PDFJSLoader.cacheViewerFiles(),
            );
        }

        try {
            await Promise.all(promisedDependencies);
            window.console.log('GENERAL_RESOURCES_CACHE: OK');
        } catch (error) {
            window.console.log('GENERAL_RESOURCES_CACHE: ERROR');
            window.console.log(error);
        }
    },

    /**
     * Returns the Symbolic Math files that need to be cached for a question set.
     * @param {Object} questionSet - The question set to check.
     * @returns {Array} - The list of Symbolic Math files to cache, or an empty array if not required.
     */
    getSymbolicMathFilesToCache(questionSet) {
        const questions = this.getAllQuestions(questionSet);

        if (requirementsParser.isSymbolicMathRequired(questions)) {
            return SymbolicMathLoader.getAllFilesToCache();
        }
        return [];
    },
};

export default generalResourcesCacher;
